import React, { useCallback, useEffect, useRef, useState } from 'react';
import { api } from './api';
import Links from './Links';
import Members from './Members';
import Setup from './Setup';
import TuiPanel from './TuiPanel';
import AccountPicker from './AccountPicker';
import Overview from './Overview';
import Positions from './Positions';
import Performance from './Performance';
import { clock, money } from './format';

const REFRESH_MS = 60000; // the server syncs on its own schedule; we just re-read

// Two ways to look at the same snapshot: "legacy" is finpilot's own terminal
// panels rendered server-side; "new" is the etched React tables.
const MODES = ['legacy', 'new'];
const readMode = () => { try { const m = localStorage.getItem('df:ui'); return MODES.includes(m) ? m : 'new'; } catch { return 'new'; } };

// finpilot's three tabs, in its order, with its key bindings.
const TABS = [
  { key: 'overview', hotkey: 'o' },
  { key: 'positions', hotkey: 'p' },
  { key: 'performance', hotkey: 'f' },
];

export default function Monitoring() {
  const [members, setMembers] = useState(null);
  const [seats, setSeats] = useState({ used: 0, limit: 5 });
  const [me, setMe] = useState(null);
  const [links, setLinks] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const accountsRef = useRef(accounts);
  accountsRef.current = accounts;

  const [member, setMember] = useState(null);     // username
  const [selected, setSelected] = useState(null); // account id
  const [tab, setTab] = useState('overview');
  const [mode, setMode_] = useState(readMode);
  const [tui, setTui] = useState(null);           // legacy: {overview, positions, performance, has_goal, taken_at}
  const [views, setViews] = useState(null);       // new: {overview, positions, performance, history}
  const [error, setError] = useState('');
  const [showLinks, setShowLinks] = useState(false);

  // `me` is fetched once; afterwards only explicit saves (keys, switches) change it,
  // so a background reload can never undo something the user just did.
  useEffect(() => {
    api('/me').then(m => { setMe(m); setMember(cur => cur || m.username); }).catch(e => setError(e.message));
  }, []);

  const load = useCallback(async () => {
    try {
      const [mem, l, a] = await Promise.all([api('/members'), api('/links'), api('/accounts')]);
      setMembers(mem.members); setSeats({ used: mem.used, limit: mem.limit }); setLinks(l); setAccounts(a); setError('');
    } catch (e) { setError(e.message); setMembers(x => x || []); }
  }, []);

  // Coming back from a broker portal: ?df=linked&link=N → finish the link.
  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    const linkId = q.get('link');
    if (q.get('df') === 'linked' && linkId) {
      window.history.replaceState({}, '', window.location.pathname);
      api(`/links/snaptrade/${linkId}/complete`, { method: 'POST' }).catch(e => setError(e.message)).finally(load);
    } else {
      if (q.get('df')) window.history.replaceState({}, '', window.location.pathname);
      load();
    }
  }, [load]);

  // Selected member's accounts; keep the account selection inside that set.
  const theirs = accounts.filter(a => a.owner === member);
  useEffect(() => {
    setSelected(s => (theirs.some(a => a.id === s) ? s : (theirs[0]?.id ?? null)));
  }, [member, accounts]); // eslint-disable-line react-hooks/exhaustive-deps

  // Only what the active mode shows is fetched.
  useEffect(() => {
    if (selected == null) { setTui(null); setViews(null); return; }
    let cancelled = false;
    const fetchAll = async () => {
      try {
        if (mode === 'legacy') {
          const t = await api(`/accounts/${selected}/tui`);
          if (cancelled) return;
          setTui(t);
        } else {
          const [o, p, f, h] = await Promise.all([
            api(`/accounts/${selected}/overview`), api(`/accounts/${selected}/positions`),
            api(`/accounts/${selected}/performance`), api(`/accounts/${selected}/history`),
          ]);
          if (cancelled) return;
          setViews({ overview: o, positions: p, performance: f, history: h });
        }
        setError('');
        // The account list may predate the first sync; catch it up so "synced" fills in.
        if (!accountsRef.current.find(a => a.id === selected)?.taken_at) load();
      } catch (e) {
        if (cancelled) return;
        if (e.status === 404) { setTui(null); setViews(null); setError('first sync running — a minute or so'); }
        else setError(e.message);
      }
    };
    fetchAll();
    const t = setInterval(() => { if (document.visibilityState === 'visible') { fetchAll(); load(); } }, REFRESH_MS);
    return () => { cancelled = true; clearInterval(t); };
  }, [selected, mode, load]);

  const setMode = m => {
    setMode_(m);
    try { localStorage.setItem('df:ui', m); } catch { /* private mode */ }
  };

  const setSharing = async on => {
    try {
      setMe(await api('/me', { method: 'PATCH', body: { share_portfolio: on } }));
      setMembers((await api('/members')).members); // my "private"/"n accounts" label changes for others; mirror it here
    } catch (e) { setError(e.message); }
  };
  const setHistory = async on => {
    if (!on && !window.confirm('delete all stored history now and keep only the latest sync from here on?')) return;
    try { setMe(await api('/me', { method: 'PATCH', body: { keep_history: on } })); } catch (e) { setError(e.message); }
  };

  const current = accounts.find(a => a.id === selected);
  const isMe = member === me?.username;
  const own = accounts.filter(a => a.is_own);
  const panel = isMe && (own.length === 0 || showLinks);

  const refresh = useCallback(async () => {
    if (!current?.is_own) return;
    try { await api(`/accounts/${current.id}/refresh`, { method: 'POST' }); setTimeout(load, 5000); } catch (e) { setError(e.message); }
  }, [current, load]);

  // finpilot's `g`: set a 3-year doubling goal; the server answers 409 with its
  // "Goal exists: …" line, which we show as-is.
  const setGoal = useCallback(async () => {
    if (!current?.is_own) return;
    try { setTui(await api(`/accounts/${current.id}/goal`, { method: 'POST' })); setError(''); } catch (e) { setError(e.message); }
  }, [current]);
  const clearGoal = async () => {
    if (!current?.is_own || !window.confirm('clear the portfolio goal?')) return;
    try { setTui(await api(`/accounts/${current.id}/goal`, { method: 'DELETE' })); setError(''); } catch (e) { setError(e.message); }
  };

  // TUI keys: o / p / f switch tabs, r refreshes, g sets the goal.
  useEffect(() => {
    if (!current || panel) return undefined;
    const onKey = e => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const tag = e.target.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || e.target.isContentEditable) return;
      const t = TABS.find(x => x.hotkey === e.key);
      if (t) { setTab(t.key); return; }
      if (e.key === 'r') refresh();
      if (e.key === 'g') setGoal();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [current, panel, refresh, setGoal]);

  if (members === null || me === null) return <p className="df-muted">loading…</p>;
  if (me.snaptrade !== 'own' && !me.fixture) {
    return <Setup onDone={m => { setMe(m); load(); }} />;
  }

  const mem = members.find(m => m.username === member);

  return (
    <section className="df-mon">
      <Members members={members} used={seats.used} limit={seats.limit} selected={member}
        onSelect={u => { setMember(u); setShowLinks(false); setTab('overview'); }}
        onChange={mem => { setMembers(mem.members); setSeats({ used: mem.used, limit: mem.limit }); }} />

      <div className="df-main">
        <div className="df-status">
          <span className="df-who">
            {isMe ? <b>you</b> : <b>{member}</b>}
            <AccountPicker accounts={theirs} selected={selected} onSelect={setSelected} />
          </span>
          <span className="df-actions">
            {current && <span className="df-muted">synced {clock(tui?.taken_at || views?.overview?.taken_at || current.taken_at)}</span>}
            {current?.is_own && <button onClick={refresh}>refresh</button>}
            {current?.is_own && mode === 'legacy' && tui && !tui.has_goal && <button onClick={setGoal}>set goal</button>}
            {current?.is_own && mode === 'legacy' && tui?.has_goal && <button onClick={clearGoal}>clear goal</button>}
            {isMe && own.length > 0 && <button onClick={() => setShowLinks(s => !s)}>{showLinks ? 'done' : 'brokers'}</button>}
          </span>
        </div>

        {panel && (
          <Links links={links} onChange={load} sharing={!!me?.share_portfolio} onSharing={setSharing} canShare={own.length > 0}
            me={me} onMe={setMe} onHistory={setHistory} />
        )}
        {error && <p className="df-error">{error}</p>}

        {!isMe && theirs.length === 0 && mem && (
          <p className="df-empty df-muted">{mem.accounts == null ? `${member} isn't sharing their portfolio.` : `${member} hasn't linked a broker yet.`}</p>
        )}

        {current && !panel && (
          <>
            <nav className="df-tabs">
              {TABS.map(t => (
                <button key={t.key} className={tab === t.key ? 'on' : ''} onClick={() => setTab(t.key)}>{t.key}</button>
              ))}
              <span className="df-keys-hint" title="keys: o overview · p positions · f performance · r refresh · g goal">o p f r g</span>
              {current.total_value != null && <span className="df-muted">{money(current.total_value)}</span>}
              <span className="df-mode" role="group" aria-label="ui">
                {MODES.map((m, i) => (
                  <React.Fragment key={m}>
                    {i > 0 && <span className="df-sep">·</span>}
                    <button className={mode === m ? 'on' : ''} aria-pressed={mode === m} onClick={() => setMode(m)}>{m}</button>
                  </React.Fragment>
                ))}
              </span>
            </nav>
            {mode === 'legacy' && tui && <TuiPanel html={tui[tab]} />}
            {mode === 'new' && views && tab === 'overview' && <Overview data={views.overview} history={views.history} />}
            {mode === 'new' && views && tab === 'positions' && <Positions data={views.positions} />}
            {mode === 'new' && views && tab === 'performance' && <Performance data={views.performance} />}
          </>
        )}
      </div>
    </section>
  );
}
